import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import EnhancedDialog from '../EnhancedDialog';
import {
  dealPrepKeys,
  fetchChainDocuments,
  runChainAction,
  type DealPrepChainAction,
  type DealPrepChainView,
} from './dealPrepApi';
import '../SendOffer/SendOfferDialog.css';
import './DealPrepHistoryDialog.css';

const TITLE: Record<DealPrepChainAction, string> = {
  resend: 'Resend deal prep',
  append_latest: 'Append latest documents',
};

function mb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * REG-859: "Resend deal prep" / "Append latest documents" for one chain. Shows
 * what goes into the chain (the newest scrubbed application and the statements
 * the CRM stored there), then queues one message. The request id is made when
 * the dialog opens, so a double click or a retry after a timeout sends once.
 */
export function DealPrepChainActionDialog({
  merchantId,
  chain,
  action,
  isOpen,
  onClose,
}: {
  merchantId: number;
  chain: DealPrepChainView;
  action: DealPrepChainAction;
  isOpen: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);
  const requestId = useMemo(() => (isOpen ? crypto.randomUUID() : ''), [isOpen]);
  const docs = useQuery({
    queryKey: ['deal-prep', 'chain-documents', chain.id],
    queryFn: () => fetchChainDocuments(merchantId, chain.id),
    enabled: isOpen,
    staleTime: 0,
  });
  useEffect(() => { if (!isOpen) setSending(false); }, [isOpen]);

  const send = async () => {
    setSending(true);
    try {
      const r = await runChainAction(action, merchantId, chain.id, requestId);
      message.success(r.duplicate
        ? 'Already queued for this DEAL PREP'
        : r.sandbox ? 'Queued (sandbox: goes to the sandbox recipient only)' : 'Queued into the DEAL PREP chain');
      await queryClient.invalidateQueries({ queryKey: dealPrepKeys.merchant(merchantId) });
      onClose();
    } catch (e) {
      message.error((e as Error).message);
      setSending(false);
    }
  };

  const data = docs.data;
  const canSend = !!data && data.files.length > 0 && !sending;
  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title={`${TITLE[action]} - ${chain.subject}`}
      className="send-offer-dialog deal-prep-dialog"
      canOutsideClickClose={false}
      canEscapeKeyClose={!sending}
    >
      <div className="sky-dialog-body" data-testid="deal-prep-chain-action-dialog">
        {docs.error && <div className="sky-callout sky-callout-warning">{(docs.error as Error).message}</div>}
        {docs.isLoading && <div className="deal-prep-loading"><span className="sky-spinner" /> Reading the chain&hellip;</div>}
        {data && (
          <>
            <p className="send-offer-hint deal-prep-hint">
              {action === 'resend'
                ? (data.card_stored
                  ? 'The DEAL PREP card goes into the chain again, with these files.'
                  : 'This chain started before the CRM sent DEAL PREPs, so a short note goes in with these files.')
                : 'A short note goes into the chain with these files.'}
              {' '}Same sender and recipients as the chain; sandbox applies.
            </p>
            {data.files.length === 0 ? (
              <div className="sky-callout sky-callout-warning" data-testid="deal-prep-action-no-files">
                The CRM has no stored documents for this DEAL PREP (files sent before the CRM took over aren&#39;t stored here).
              </div>
            ) : (
              <ul className="deal-prep-action-files" data-testid="deal-prep-action-files">
                {data.files.map((f) => (
                  <li key={f.filename}>{f.filename} <span className="sky-dp-sub">({f.role}, {mb(f.size_bytes)})</span></li>
                ))}
              </ul>
            )}
            {!data.has_application && data.files.length > 0 && (
              <div className="sky-callout sky-callout-warning">
                {data.unscrubbed_only
                  ? 'Only the unscrubbed application is stored, so no application goes with it.'
                  : 'No scrubbed application is stored for this chain.'}
              </div>
            )}
            {data.skipped.length > 0 && (
              <div className="sky-dp-sub">Left out (over 20 MB or a repeated name): {data.skipped.map((f) => f.filename).join(', ')}</div>
            )}
          </>
        )}
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={onClose} disabled={sending}>Cancel</button>
        <button className="sky-btn sky-btn-primary" onClick={send} disabled={!canSend} aria-busy={sending}
          data-testid="deal-prep-action-send">
          {sending ? <span className="sky-spinner" /> : null}
          {sending ? 'Queuing…' : TITLE[action]}
        </button>
      </div>
    </EnhancedDialog>
  );
}

export default DealPrepChainActionDialog;
